import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, t } from '../../context/LanguageContext';
import driverPortalService from '../../services/driverPortalService';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export default function PortalDriverOffersPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [available, setAvailable] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [error, setError] = useState('');
  const [pushState, setPushState] = useState('checking'); // checking | prompt | subscribed | unsupported | denied
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(() => {
    driverPortalService.getOffers().then(setOffers).catch(() => {});
  }, []);

  useEffect(() => {
    driverPortalService.getMe().then(me => setAvailable(!!me.isAvailableForDelivery)).catch(() => {}).finally(() => setLoading(false));
    load();
    const interval = setInterval(load, 15000); // catch offers even if push didn't fire
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushState('unsupported'); return; }
      if (Notification.permission === 'denied') { setPushState('denied'); return; }
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = reg && await reg.pushManager.getSubscription();
        setPushState(sub ? 'subscribed' : 'prompt');
      } catch { setPushState('prompt'); }
    })();
  }, []);

  const enablePush = async () => {
    setPushBusy(true); setError('');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setPushState('denied'); return; }
      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) reg = await navigator.serviceWorker.register('/sw.js');
      const publicKey = await driverPortalService.getVapidPublicKey();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
      }
      await driverPortalService.subscribePush(sub);
      setPushState('subscribed');
    } catch (err) {
      setError(t('Could not enable notifications. Try again.', 'No se pudieron activar las notificaciones. Intenta de nuevo.', lang));
    } finally {
      setPushBusy(false);
    }
  };

  const toggleAvailable = async () => {
    const next = !available;
    setAvailable(next); // optimistic
    try {
      await driverPortalService.setAvailability(next);
    } catch (err) {
      setAvailable(!next);
      setError(err?.response?.data?.message || 'Failed to update availability.');
    }
  };

  const acceptOffer = (offer) => {
    setAccepting(offer.id); setError('');
    const finish = (latitude, longitude) => {
      driverPortalService.acceptDelivery(offer.id, latitude, longitude)
        .then(() => navigate('/portal/driver/deliveries'))
        .catch(err => setError(err?.response?.data?.message || 'Could not accept — it may have already been taken.'))
        .finally(() => setAccepting(null));
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => finish(pos.coords.latitude, pos.coords.longitude),
        () => finish(null, null),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else {
      finish(null, null);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">{t('Delivery Offers', 'Ofertas de Entrega', lang)}</h6>
        <div className="form-check form-switch mb-0">
          <input className="form-check-input" type="checkbox" role="switch" checked={available} onChange={toggleAvailable} style={{ width: 42, height: 22 }} />
          <label className="form-check-label" style={{ fontSize: 13 }}>
            {available ? t('Available', 'Disponible', lang) : t('Offline', 'Sin conexión', lang)}
          </label>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      {pushState === 'prompt' && (
        <div className="alert alert-primary py-2 px-3 mb-3 d-flex align-items-center justify-content-between gap-2" style={{ fontSize: 13 }}>
          <span>🔔 {t('Enable notifications to get delivery offers instantly.', 'Activa las notificaciones para recibir ofertas al instante.', lang)}</span>
          <button className="btn btn-primary btn-sm flex-shrink-0" onClick={enablePush} disabled={pushBusy}>
            {pushBusy ? <span className="spinner-border spinner-border-sm" /> : t('Enable', 'Activar', lang)}
          </button>
        </div>
      )}
      {pushState === 'denied' && (
        <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 13 }}>
          {t('Notifications are blocked in your browser (this is also always the case in Incognito/Private mode) — you\'ll only see new offers when you open this page.', 'Las notificaciones están bloqueadas en tu navegador (esto también ocurre siempre en modo Incógnito/Privado) — solo verás las ofertas nuevas al abrir esta página.', lang)}
        </div>
      )}
      {pushState === 'subscribed' && (
        <div className="text-success mb-3" style={{ fontSize: 12 }}>
          🔔 {t('Notifications enabled — you\'ll get an alert when a new offer comes in.', 'Notificaciones activadas — recibirás una alerta cuando llegue una oferta nueva.', lang)}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
      ) : !available ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 40, marginBottom: 8 }}>💤</div>
          <p style={{ fontSize: 14 }}>{t('Turn on availability to see and accept delivery offers.', 'Activa tu disponibilidad para ver y aceptar ofertas.', lang)}</p>
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 40, marginBottom: 8 }}>📦</div>
          <p style={{ fontSize: 14 }}>{t('No open offers right now.', 'No hay ofertas abiertas por ahora.', lang)}</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {offers.map(offer => (
            <div key={offer.id} className="card shadow-sm">
              <div className="card-body" style={{ padding: '12px 14px' }}>
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <div className="fw-bold" style={{ fontSize: 14 }}>#{offer.Order?.orderNumber}</div>
                  <span className="badge bg-success" style={{ fontSize: 13 }}>${Number(offer.payoutAmountUsd || 0).toFixed(2)}</span>
                </div>
                <div style={{ fontSize: 13, color: '#495057' }}>
                  {[offer.Order?.deliveryCity, offer.Order?.deliveryState].filter(Boolean).join(', ') || t('Delivery address on acceptance', 'Dirección al aceptar', lang)}
                </div>
                <button className="btn btn-primary btn-sm mt-2 w-100" onClick={() => acceptOffer(offer)} disabled={accepting === offer.id}>
                  {accepting === offer.id ? <span className="spinner-border spinner-border-sm" /> : t('Accept', 'Aceptar', lang)}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
